import { FC } from 'react';
import { TASK_ARCHIVE_AGE_IN_DAYS, TASK_DELETE_AGE_IN_MONTHS } from '@frontend/call-intel-core';
import { Trans, useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { TextLink } from '@frontend/design-system';

type TaskArchivalMessageVariant =
  | 'archived'
  | 'archived-with-metrics'
  | 'archived-and-deleted'
  | 'archived-adjust-filter'
  | 'archived-and-deleted-adjust-filter'
  | 'deleted-adjust-filter';

type TaskArchivalMessageProps = {
  variant: TaskArchivalMessageVariant;
  linkTaskCenter?: boolean;
};

export const TaskArchivalMessage: FC<TaskArchivalMessageProps> = ({ variant, linkTaskCenter = false }) => {
  const { t } = useTranslation('call-intelligence');
  const { setIsTaskCenterOpen } = useTaskCenterStore(['setIsTaskCenterOpen']);

  switch (variant) {
    case 'archived':
      if (!linkTaskCenter) {
        return (
          <>
            {t('Tasks inactive for {{count}} days and past due are archived in the Task Center.', {
              count: TASK_ARCHIVE_AGE_IN_DAYS,
            })}
          </>
        );
      }

      return (
        <Trans t={t} count={TASK_ARCHIVE_AGE_IN_DAYS}>
          Tasks inactive for {{ count: TASK_ARCHIVE_AGE_IN_DAYS }} days and past due are archived in the{' '}
          <TextLink onClick={() => setIsTaskCenterOpen(true)}>Task Center</TextLink>.
        </Trans>
      );

    case 'archived-with-metrics':
      if (!linkTaskCenter) {
        return (
          <>
            {t(
              'Tasks inactive for {{count}} days and past due are archived in the Task Center. Archived tasks do not change your task metrics.',
              {
                count: TASK_ARCHIVE_AGE_IN_DAYS,
              }
            )}
          </>
        );
      }

      return (
        <Trans t={t} count={TASK_ARCHIVE_AGE_IN_DAYS}>
          Tasks inactive for {{ count: TASK_ARCHIVE_AGE_IN_DAYS }} days and past due are archived in the{' '}
          <TextLink onClick={() => setIsTaskCenterOpen(true)}>Task Center</TextLink>
          {'. '}Archived tasks do not change your task metrics.
        </Trans>
      );

    case 'archived-and-deleted':
      if (!linkTaskCenter) {
        return (
          <>
            {t(
              'Tasks inactive for {{count}} days and past due are archived in the Task Center. Archived tasks are automatically deleted after {{months}} months.',
              {
                count: TASK_ARCHIVE_AGE_IN_DAYS,
                months: TASK_DELETE_AGE_IN_MONTHS,
              }
            )}
          </>
        );
      }

      return (
        <Trans t={t} count={TASK_ARCHIVE_AGE_IN_DAYS}>
          Tasks inactive for {{ count: TASK_ARCHIVE_AGE_IN_DAYS }} days and past due are archived in the{' '}
          <TextLink onClick={() => setIsTaskCenterOpen(true)}>Task Center</TextLink>
          {'. '}Archived tasks are automatically deleted after {{ months: TASK_DELETE_AGE_IN_MONTHS }} months.
        </Trans>
      );

    case 'archived-adjust-filter':
      return (
        <Trans t={t} count={TASK_ARCHIVE_AGE_IN_DAYS}>
          Tasks inactive for {{ count: TASK_ARCHIVE_AGE_IN_DAYS }} days and past due are archived in the{' '}
          <TextLink onClick={() => setIsTaskCenterOpen(true)}>Task Center</TextLink>
          {'. '}Adjust your time period filter to view more recent tasks.
        </Trans>
      );

    case 'archived-and-deleted-adjust-filter':
      return (
        <Trans t={t} count={TASK_ARCHIVE_AGE_IN_DAYS}>
          Tasks inactive for {{ count: TASK_ARCHIVE_AGE_IN_DAYS }} days and past due are archived in the{' '}
          <TextLink onClick={() => setIsTaskCenterOpen(true)}>Task Center</TextLink>
          {'. '}Archived tasks are automatically deleted after {{ months: TASK_DELETE_AGE_IN_MONTHS }} months. Adjust
          your time period filter to view more recent tasks.
        </Trans>
      );

    case 'deleted-adjust-filter':
      return (
        <>
          {t(
            'Archived tasks are automatically deleted after {{months}} months. Adjust your time period filter to view more recent tasks.',
            {
              months: TASK_DELETE_AGE_IN_MONTHS,
            }
          )}
        </>
      );

    default:
      return null;
  }
};
